import { Box, Button, Text, VStack } from '@chakra-ui/react';
import { LuArrowRight } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatRelativeAgo } from '../../../utils/intlFormatters';
import { offByText } from '../repairsUI';
import { CHECKUP_PATH } from './CheckupBanner';

/**
 * The checkup in a statement's detail panel (and the phone's sheet): what
 * its flags mean, in a sentence each, and a way into the checkup for it:
 * "Review" when it needs a look, "See what changed" once a check of it was
 * applied, else "Check this statement" when it can be checked at all.
 * `flags` is the statement's row of `GET /repairs/statements`.
 */
export default function StatementReviewCard({ sourceFileId, flags, phone = false }) {
  const { t } = useTranslation('repairs');
  const navigate = useNavigate();
  const sentences = [
    flags.reprocess_suggested && t('checkup.card.reprocess'),
    flags.adds_up === false && [
      t('checkup.card.doesntAddUp'),
      ...(flags.off_by || []).map((offBy) => t('checkup.card.offBy', { amount: offByText(offBy) })),
      flags.balance_matches && t('checkup.card.balanceMatches'),
    ].filter(Boolean).join(' '),
    flags.leftover_rows > 0 && t(
      flags.leftover_rows === 1 ? 'checkup.card.leftoversOne' : 'checkup.card.leftoversOther',
      { count: flags.leftover_rows },
    ),
    flags.repaired && t('checkup.card.repaired'),
    flags.reviewed && t('checkup.card.reviewed', { when: formatRelativeAgo(flags.reviewed.at) }),
  ].filter(Boolean);
  const applied = flags.latest_run?.status === 'applied';
  const open = () => navigate(`${CHECKUP_PATH}?statement=${sourceFileId}`);
  const action = flags.needs_attention || applied || flags.checkable;

  return (
    <Box>
      <Text fontSize={phone ? '15px' : 'sm'} fontWeight={600} color="redesign.heading" mb={2}>{t('checkup.card.title')}</Text>
      <VStack align="stretch" spacing={1} mb={action ? 3 : 0}>
        {sentences.length
          ? sentences.map((sentence) => (
            <Text key={sentence} fontSize={phone ? '14px' : 'sm'} color={flags.needs_attention ? 'redesign.heading' : 'redesign.text'}>
              {sentence}
            </Text>
          ))
          : <Text fontSize={phone ? '14px' : 'sm'} color="redesign.text.muted">{t('checkup.card.nothing')}</Text>}
      </VStack>
      {flags.needs_attention ? (
        <Button size={phone ? 'md' : 'sm'} colorScheme="blue" rightIcon={<LuArrowRight />} onClick={open} w={phone ? '100%' : 'auto'}>
          {t('checkup.card.review')}
        </Button>
      ) : applied ? (
        <Button size={phone ? 'md' : 'sm'} variant="outline" onClick={open} w={phone ? '100%' : 'auto'}>
          {t('checkup.card.seeChanges')}
        </Button>
      ) : flags.checkable && (
        <Button size={phone ? 'md' : 'sm'} variant="outline" onClick={open} w={phone ? '100%' : 'auto'}>
          {t('checkup.card.check')}
        </Button>
      )}
    </Box>
  );
}
